import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/** A dropdown filter (Status, Payment method, ...). */
export interface ListFilterSelect {
  /** Form control name and the key used in the emitted values object. */
  key: string;
  label: string;
  /** Label for the "no selection" option (defaults to "All"). */
  allLabel?: string;
  options: { value: string; label: string }[];
}

/** Declares which filter fields a host list wants to show. */
export interface ListFilterConfig {
  search?: boolean | { placeholder?: string };
  /** Renders a Start date + End date pair. */
  dateRange?: boolean;
  selects?: ListFilterSelect[];
  /** Renders a Minimum + Maximum amount pair. */
  amountRange?: boolean;
}

/** Normalized, backend-ready filter values. Empty fields are omitted. */
export interface ListFilterValues {
  search?: string;
  from?: string;
  to?: string;
  minAmount?: number;
  maxAmount?: number;
  [selectKey: string]: string | number | undefined;
}

/**
 * Reusable, config-driven filter bar for list/dashboard pages. Emits a
 * normalized {@link ListFilterValues} the host maps straight into its service
 * query — filtering always happens on the server, never on the loaded rows.
 */
@Component({
  selector: 'app-list-filters',
  standalone: false,
  templateUrl: './list-filters.component.html',
  styleUrl: './list-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFiltersComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);

  @Input({ required: true }) config!: ListFilterConfig;
  @Output() readonly filtersChange = new EventEmitter<ListFilterValues>();

  form!: FormGroup;

  private readonly destroy$ = new Subject<void>();

  get searchPlaceholder(): string {
    return typeof this.config.search === 'object'
      ? (this.config.search.placeholder ?? 'Search')
      : 'Search';
  }

  ngOnInit(): void {
    const controls: Record<string, unknown[]> = {};

    if (this.config.search) controls['search'] = [''];
    if (this.config.dateRange) {
      controls['from'] = [null];
      controls['to'] = [null];
    }
    for (const select of this.config.selects ?? []) {
      controls[select.key] = [''];
    }
    if (this.config.amountRange) {
      controls['minAmount'] = [null];
      controls['maxAmount'] = [null];
    }

    this.form = this.fb.group(controls);

    this.form.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.emit());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Explicit search trigger (search icon button). */
  applyNow(): void {
    this.emit();
  }

  reset(): void {
    this.form.reset(
      Object.keys(this.form.controls).reduce<Record<string, unknown>>(
        (acc, key) => {
          acc[key] = key === 'search' || this.isSelectKey(key) ? '' : null;
          return acc;
        },
        {},
      ),
    );
    this.emit();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.form?.value ?? {}).some(
      (value) => value !== null && value !== '' && value !== undefined,
    );
  }

  private isSelectKey(key: string): boolean {
    return (this.config.selects ?? []).some((s) => s.key === key);
  }

  private emit(): void {
    const raw = this.form.getRawValue();
    const values: ListFilterValues = {};

    if (this.config.search) {
      const search = String(raw['search'] ?? '').trim();
      if (search) values.search = search;
    }
    if (this.config.dateRange) {
      const from = this.toIsoDate(raw['from']);
      const to = this.toIsoDate(raw['to']);
      if (from) values.from = from;
      if (to) values.to = to;
    }
    for (const select of this.config.selects ?? []) {
      const value = raw[select.key];
      if (value !== '' && value !== null && value !== undefined) {
        values[select.key] = value;
      }
    }
    if (this.config.amountRange) {
      const min = this.toNumber(raw['minAmount']);
      const max = this.toNumber(raw['maxAmount']);
      if (min !== undefined) values.minAmount = min;
      if (max !== undefined) values.maxAmount = max;
    }

    this.filtersChange.emit(values);
  }

  /** Convert a Date (or ISO-ish string) to `yyyy-MM-dd`; null-ish → undefined. */
  private toIsoDate(value: unknown): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toNumber(value: unknown): number | undefined {
    if (value === '' || value === null || value === undefined) return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
}
